<template>
  <div>
    <div class="list mb-3 gap-4">
      <div
        v-for="dependingIncident of dependingIncidents"
        :key="dependingIncident.mainCondition.id"
      >
        <depending-incident-trigger
          :depending-incident="dependingIncident"
          :source="activator"
          :selected-condition="selectedCondition"
          :open="openStates[dependingIncident.mainCondition.id] || false"
          @update:open="openStates[dependingIncident.mainCondition.id] = $event"
          @select="emit('select', $event)"
        />
      </div>
    </div>

    <input-dropdown
      ref="dependingselect"
      :options="followableIncidents"
      :order="incidentsOrder"
      :clear-on-select="true"
      :placeholder="`+ ${$t('labels.new')}`"
      :searchable="true"
      prop="title"
      @select="addDependingIncident"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { getDependingIncidents } from "./incidentFunctions";

import { useUndo } from "@editor/store/undo/undo.store";
import { useIncidentStore } from "./incidents.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";

import InputDropdown from "@core/input/InputDropdown.vue";
import DependingIncidentTrigger from "./DependingIncidentTrigger.vue";

import type { IncidentItem } from "./IncidentItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";

const props = withDefaults(
  defineProps<{
    activator: ActionItem | IncidentItem;
    selectedCondition?: { type: "condition"; id: number };
  }>(),
  {
    selectedCondition: undefined
  }
);

const emit = defineEmits<{
  (event: "select", condition: ConditionItem | IncidentItem): void;
  (event: "created", item: IncidentItem): void;
}>();

const { t } = useI18n();
const undoStore = useUndo();

const incidentTriggerStore = useIncidentTriggerStore();
const conditionStore = useConditionStore();
const incidentStore = useIncidentStore();

const openStates = reactive({} as { [key: number]: boolean });

const dependingIncidents = computed(() => {
  return getDependingIncidents(props.activator);
});

const followableIncidents = computed(() => {
  // all incidents + "new incident"
  const incidents: { title: string; id: number | null }[] =
    incidentStore.orderedItems.slice();
  incidents.push({
    title: t("labels.newIncident"),
    id: null
  });
  return incidents;
});

const incidentsOrder = computed(() => {
  return incidentStore.order;
});

async function addDependingIncident({ item }: { item: { id: number } }) {
  let id = item.id;

  undoStore.startGroup("model", "addDependingIncident");

  // create an incident if the passed incident doesn't have an id.
  if (id == null) {
    const incident = await incidentStore.create({});
    id = incident.id;
    emit("created", incident);
  }

  const trigger = await incidentTriggerStore.create({
    incidentId: id,
    chance: 100
  });

  const newCondition = await conditionStore.createValid({
    activatorType: props.activator.type,
    activatorId: props.activator.id,
    activatorProperty: "activationCount",

    activatorEndType: "currentRound",

    srcId: trigger.id,
    srcType: trigger.type,

    operator: "?",

    comparedType: "value"
  });

  openStates[newCondition.id] = true;

  undoStore.endGroup("model");
}
</script>
